import { useState, useCallback } from "react";
import {
  UploadResultEntity,
  DocumentType,
  UploadDocumentsUseCase,
} from "../../domain";
import { DocumentRepositoryImpl } from "../../data/repositories/DocumentRepositoryImpl";
import { getCompany } from "../../../../shared/data/storage/authStorage";

// Create repository instance (singleton)
const documentRepository = new DocumentRepositoryImpl();

// Create use cases
const uploadDocumentsUseCase = new UploadDocumentsUseCase(documentRepository);

export interface UploadStatus {
  type: "success" | "error" | null;
  message: string;
}

export interface UseUploadReturn {
  // Upload functions
  uploadDocuments: (
    files: File[],
    documentType: DocumentType
  ) => Promise<UploadResultEntity>;

  // Upload states
  uploadingBills: boolean;
  uploadingInvoices: boolean;

  // Status
  uploadStatus: UploadStatus;
  setSuccessStatus: (message: string) => void;
  clearUploadStatus: () => void;

  // Error
  error: string | null;
  clearError: () => void;
}

export const useUpload = (onUploadSuccess?: () => void): UseUploadReturn => {
  const [uploadingBills, setUploadingBills] = useState(false);
  const [uploadingInvoices, setUploadingInvoices] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    type: null,
    message: "",
  });
  const [error, setError] = useState<string | null>(null);

  /**
   * Upload documents (invoices/bills)
   */
  const uploadDocuments = useCallback(
    async (
      files: File[],
      documentType: DocumentType
    ): Promise<UploadResultEntity> => {
      const isInvoice = documentType === "invoice";
      isInvoice ? setUploadingInvoices(true) : setUploadingBills(true);
      setError(null);
      setUploadStatus({ type: null, message: "" });

      try {
        const company = getCompany();
        if (!company || !company.zohoOrgId) {
          throw new Error("Company data not found");
        }

        const result = await uploadDocumentsUseCase.execute(
          files,
          documentType,
          company.zohoOrgId
        );

        setUploadStatus({
          type: "success",
          message: `Successfully uploaded ${files.length} file${
            files.length > 1 ? "s" : ""
          }`,
        });

        // Call success callback to refresh documents
        onUploadSuccess?.();

        return result;
      } catch (error: any) {
        const errorMessage = error.message || "Upload failed";
        setError(errorMessage);
        setUploadStatus({
          type: "error",
          message: errorMessage,
        });
        throw error;
      } finally {
        isInvoice ? setUploadingInvoices(false) : setUploadingBills(false);
      }
    },
    [onUploadSuccess]
  );

  /**
   * Set upload status (for external use)
   */
  const setSuccessStatus = useCallback((message: string) => {
    setUploadStatus({ type: "success", message });
  }, []);

  /**
   * Clear upload status
   */
  const clearUploadStatus = useCallback(() => {
    setUploadStatus({ type: null, message: "" });
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    uploadDocuments,
    uploadingBills,
    uploadingInvoices,
    uploadStatus,
    setSuccessStatus,
    clearUploadStatus,
    error,
    clearError,
  };
};
